import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton } from '@material-ui/core'
import Card from '../../../../../molecules/Card'
import SydLabel from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydButton from '../../../../../commonDesign/Button'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'
import { decipherError } from '../../../../../../utils/decipherError'
import {
  sectionHeaderStyle,
  rowStyle,
  errorStyle,
  emptyStyle,
  descriptionStyle,
  getDefinition,
  normalizeKeyArray,
  useSaveDefinitionSection
} from './shared'

const autoLabelStyle = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: '4px',
  fontSize: '0.6875rem',
  fontWeight: 600,
  background: 'var(--color-neutral-100)',
  color: 'var(--color-neutral-600)'
}

function getAutoIndexes (definition) {
  const partitionKey = definition.partitionKey || []
  const domainKey = normalizeKeyArray(definition.domainKey)
  const allKeyColumns = [...partitionKey, ...domainKey]

  const auto = []
  auto.push({
    name: 'PRIMARY',
    columns: allKeyColumns.map(k => k.name),
    source: 'Primary Key'
  })
  if (partitionKey.length > 0) {
    auto.push({
      name: `LIST (${partitionKey.map(k => k.name).join(', ')})`,
      columns: partitionKey.map(k => k.name),
      source: 'Partition Scheme'
    })
  }
  return auto
}

function EditIndexesModal ({ open, onClose, target }) {
  const saveSection = useSaveDefinitionSection(target)
  const [rows, setRows] = useState([])
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    if (open) {
      const def = getDefinition(target)
      setRows((def.indexes || []).map(i => ({
        name: i.name || '',
        columnsText: (i.columns || []).join(', ')
      })))
      setError(null)
    }
  }, [open, target])

  const handleChange = useCallback((i, field, value) => {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r))
  }, [])

  const handleAdd = useCallback(() => {
    setRows(prev => [...prev, { name: '', columnsText: '' }])
  }, [])

  const handleRemove = useCallback((i) => {
    setRows(prev => prev.filter((_, idx) => idx !== i))
  }, [])

  const handleSave = useCallback(async () => {
    for (const r of rows) {
      if (!r.name || !r.columnsText.trim()) { setError('All indexes must have a name and at least one column'); return }
    }
    const indexes = rows.map(r => ({
      name: r.name,
      columns: r.columnsText.split(',').map(s => s.trim()).filter(Boolean)
    }))
    try {
      setError(null)
      setProcessing(true)
      await saveSection({ indexes })
      onClose?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [rows, saveSection, onClose])

  return (
    <SydModal open={open} onClose={onClose} title='Edit Secondary Indexes' size='medium'>
      <div style={descriptionStyle}>
        Secondary indexes improve query performance on specific columns. The primary key index
        (partition key + domain key) and the LIST partition scheme are created automatically &mdash; you
        do not need to add those here. Column names must match columns that exist on the table (including virtual stored columns).
      </div>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'flex-end' }}>
        <SydButton variant='outline' size='sm' icon='add' onClick={handleAdd}>Add Index</SydButton>
      </div>
      {rows.map((item, i) => (
        <div key={i} style={rowStyle}>
          <Grid container spacing={1} alignItems='flex-end'>
            <Grid item xs={4}>
              <SydLabel label='Index Name'>
                <SydInput size='sm' value={item.name} onChange={e => handleChange(i, 'name', e.target.value)} placeholder='idx_name' minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={7}>
              <SydLabel label='Columns (comma-separated)'>
                <SydInput size='sm' value={item.columnsText} onChange={e => handleChange(i, 'columnsText', e.target.value)} placeholder='col1, col2' minWidth='0' />
              </SydLabel>
            </Grid>
            <Grid item xs={1} style={{ textAlign: 'right' }}>
              <IconButton size='small' onClick={() => handleRemove(i)} title='Remove'>
                <Icon name={ICON_NAMES.close} customSize='16px' />
              </IconButton>
            </Grid>
          </Grid>
        </div>
      ))}
      {!rows.length && <div style={emptyStyle}>No indexes defined</div>}
      <SydModalActions>
        {error ? <div style={errorStyle}>{error}</div> : null}
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onClose}>Cancel</SydButton>
        <SydButton processing={processing} variant='primary' size='md' onClick={handleSave}>Save</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

EditIndexesModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  target: PropTypes.object
}

export default function IndexesCard ({ target }) {
  const [editing, setEditing] = useState(false)
  const definition = getDefinition(target)
  const indexes = definition.indexes || []
  const autoIndexes = getAutoIndexes(definition)
  const totalCount = autoIndexes.length + indexes.length

  return (
    <Card>
      <div style={sectionHeaderStyle}>
        <h3>Indexes ({totalCount})</h3>
        <IconButton size='small' onClick={() => setEditing(true)} title='Edit Indexes'>
          <Icon name={ICON_NAMES.edit} customSize='18px' />
        </IconButton>
      </div>
      <table className='opal-table'>
        <thead>
          <tr><th>Name</th><th>Columns</th><th>Source</th></tr>
        </thead>
        <tbody>
          {autoIndexes.map((idx, i) => (
            <tr key={`auto-${i}`} style={{ opacity: 0.7 }}>
              <td><code>{idx.name}</code></td>
              <td>{idx.columns.join(', ')}</td>
              <td><span style={autoLabelStyle}>{idx.source}</span></td>
            </tr>
          ))}
          {indexes.length > 0 && (
            <tr>
              <td colSpan={3} style={{ padding: '4px 8px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', background: 'var(--color-neutral-50)' }}>
                Secondary Indexes
              </td>
            </tr>
          )}
          {indexes.map((idx, i) => (
            <tr key={`user-${i}`}>
              <td><code>{idx.name}</code></td>
              <td>{(idx.columns || []).join(', ')}</td>
              <td><span style={autoLabelStyle}>User Defined</span></td>
            </tr>
          ))}
          {!indexes.length && (
            <tr>
              <td colSpan={3} style={{ fontStyle: 'italic', color: 'var(--color-neutral-600)' }}>
                No additional secondary indexes defined
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <EditIndexesModal open={editing} onClose={() => setEditing(false)} target={target} />
    </Card>
  )
}

IndexesCard.propTypes = { target: PropTypes.object.isRequired }
